import { useState } from "react";
import { cache, request } from "../../utils";
import { User } from "../../stores/useUserStore";
import { Modal } from "../ui/modal";
import Alert from "../ui/alert/Alert";
import PhoneNumberInput from "../form/input/PhoneNumberInput";
import Label from "../form/Label";
import Button from "../ui/button/Button";
import { useNavigate } from "react-router";


interface AddPhoneNumberProps {
  isOpen?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
}

export default function AddPhoneNumber({
  isOpen,
  onClose,
  onSuccess,
}: AddPhoneNumberProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();


  const modalOpen = isOpen ?? internalOpen;

  const resetForm = () => {
    setPhoneNumber("");
    setError("");
    setSuccess(false);
    setIsLoading(false);
  };

  const openModal = () => {
    if (isOpen === undefined) {
      setInternalOpen(true);
    }
  };

  const closeModal = () => {
    if (isOpen === undefined) {
      setInternalOpen(false);
    }
    onClose?.();
    resetForm();
    navigate("/dashboard");
  };

  const validatePhoneNumber = (phone: string) =>
    /^\+?[\d\s-()]{10,}$/.test(phone);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!phoneNumber.trim()) {
      setError("Phone number is required");
      return;
    }

    if (!validatePhoneNumber(phoneNumber)) {
      setError("Please enter a valid phone number");
      return;
    }

    setIsLoading(true);

    try {
      const response = await request.patch({
        url: "/user/phone-number",
        data: { phoneNumber: phoneNumber.trim() },
      });

      if (response.success) {
        const user = cache.get("USER") as User;
        if (user) {
          cache.set("USER", { ...user, phoneNumber: phoneNumber.trim() });
        }

        setSuccess(true);

        setTimeout(() => {
          closeModal();
          onSuccess?.();
        }, 1500);
      }
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* ---- Self-open Card (only when uncontrolled) */}
      {isOpen === undefined && (
        <div className="p-5 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
          <div className="flex flex-col items-center gap-6 lg:flex-row lg:justify-between">
            <div className="text-center lg:text-left">
              <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Add your Phone Number
              </h4>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Add a phone number to secure your account
              </p>
            </div>

            <button
              onClick={openModal}
              className="flex w-full items-center justify-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03]"
            >
              Add Phone Number
            </button>
          </div>
        </div>
      )}

      {/* ---- Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        className="max-w-[500px] m-4"
      >
        <div className="rounded-3xl bg-white p-6 dark:bg-gray-900">
          <h4 className="mb-2 text-2xl font-semibold text-gray-800 dark:text-white/90">
            Add Phone Number
          </h4>
          <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
            Please add your phone number to continue.
          </p>

          {success && (
            <Alert
              variant="success"
              title="Success"
              message="Phone number added successfully"
            />
          )}

          {error && (
            <Alert variant="error" title="Error" message={error} />
          )}

          <form onSubmit={handleSubmit}>
            <Label>Phone Number</Label>
            <PhoneNumberInput
              value={phoneNumber}
              onChange={setPhoneNumber}
              defaultCountry="AF"
              required
            />

            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={closeModal}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button size="sm" type="submit" disabled={isLoading}>
                {isLoading ? "Adding..." : "Add Phone Number"}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}
